import React, { useEffect, useRef, useState } from "react";
import { App, Typography } from "antd";
import {
  CloseOutlined,
  PlusOutlined,
  RotateRightOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import HeaderLayout from "../../components/Header";
import FooterLayout from "../../components/Footer";
import PageHeader from "../../components/PageHeader";
import OfficePdfResultActions from "../../components/OfficePdfResultActions";
import "./Image2Pdf.css";

const { Title, Paragraph } = Typography;

const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/bmp",
  "image/tiff",
  "image/gif",
  "image/webp",
];

const createImageItem = (file) => ({
  id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()
    .toString(36)
    .slice(2, 8)}`,
  file,
  previewUrl: URL.createObjectURL(file),
  rotation: 0,
});

const readImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

const canvasToBlob = (canvas, type) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Unable to process image"));
        return;
      }
      resolve(blob);
    }, type);
  });

const rotateImageFile = async (item) => {
  const normalizedRotation = ((item.rotation % 360) + 360) % 360;
  if (normalizedRotation === 0) {
    return item.file;
  }

  const image = await readImage(item.previewUrl);
  const radians = (normalizedRotation * Math.PI) / 180;
  const swapSides = normalizedRotation === 90 || normalizedRotation === 270;
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  canvas.width = swapSides ? image.height : image.width;
  canvas.height = swapSides ? image.width : image.height;

  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate(radians);
  context.drawImage(image, -image.width / 2, -image.height / 2);

  const type = ACCEPTED_IMAGE_TYPES.includes(item.file.type)
    ? item.file.type
    : "image/png";
  const blob = await canvasToBlob(canvas, type);

  return new File([blob], item.file.name, {
    type,
    lastModified: item.file.lastModified,
  });
};

const Image2Pdf = () => {
  const { message } = App.useApp();
  const [imageItems, setImageItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [convertedFileName, setConvertedFileName] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef(null);
  const imageItemsRef = useRef([]);
  const isModule = window.location.pathname.includes("/dashboard/");

  useEffect(() => {
    imageItemsRef.current = imageItems;
  }, [imageItems]);

  useEffect(() => {
    return () => {
      imageItemsRef.current.forEach((item) =>
        URL.revokeObjectURL(item.previewUrl)
      );
    };
  }, []);

  const resetResultState = () => {
    setDownloadUrl("");
    setConvertedFileName("");
  };

  const appendFiles = (files) => {
    if (uploading) {
      return;
    }

    const validFiles = Array.from(files || []).filter((file) =>
      file.type.startsWith("image/")
    );

    if (validFiles.length === 0) {
      message.warning("Please select valid image files.");
      return;
    }

    resetResultState();
    setImageItems((prevItems) => [
      ...prevItems,
      ...validFiles.map((file) => createImageItem(file)),
    ]);
  };

  const openFilePicker = () => {
    if (uploading) {
      return;
    }
    inputRef.current?.click();
  };

  const handleFileInput = (event) => {
    appendFiles(event.target.files);
    event.target.value = "";
  };

  const handleRemove = (id) => {
    if (uploading) {
      return;
    }

    resetResultState();
    setImageItems((prevItems) => {
      const itemToRemove = prevItems.find((item) => item.id === id);
      if (itemToRemove) {
        URL.revokeObjectURL(itemToRemove.previewUrl);
      }
      return prevItems.filter((item) => item.id !== id);
    });
  };

  const handleRotate = (id) => {
    if (uploading) {
      return;
    }

    resetResultState();
    setImageItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id
          ? { ...item, rotation: (item.rotation + 90) % 360 }
          : item
      )
    );
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (uploading) {
      return;
    }
    setIsDragOver(false);
    appendFiles(event.dataTransfer.files);
  };

  const handleConvert = async () => {
    if (imageItems.length === 0) {
      message.error("Please select at least one image file.");
      return;
    }

    const formData = new FormData();
    formData.append("Source", isModule ? "Private-Web" : "Public-Web");

    try {
      setUploading(true);
      resetResultState();

      const processedFiles = await Promise.all(
        imageItems.map((item) => rotateImageFile(item))
      );

      processedFiles.forEach((file) => {
        formData.append("files", file);
      });

      const requestConfig = {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(isModule
            ? { Authorization: `Bearer ${localStorage.getItem("token") || ""}` }
            : {}),
        },
      };

      const endpoint = isModule
        ? `${process.env.REACT_APP_BASE_URL}/api/Pdf/image-to-pdf`
        : `${process.env.REACT_APP_BASE_URL}/api/WebsitePdf/website-image-to-pdf`;

      const response = await axios.post(endpoint, formData, requestConfig);

      if (response.data && !response.data.error && response.data.data?.downloadUrl) {
        setDownloadUrl(response.data.data.downloadUrl);
        setConvertedFileName(
          response.data.data.fileName ||
            (imageItems.length === 1 ? imageItems[0].file.name : "images.pdf")
        );
        message.success(
          response.data.message || "Images converted successfully!"
        );
      } else {
        message.error(response.data.message || "Unknown error occurred.");
      }
    } catch (error) {
      console.error(error);
      message.error("Conversion failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      {isModule ? <PageHeader /> : <HeaderLayout />}

      <div className={`i2p-root ${isModule ? "i2p-module" : ""}`}>
        <div className="i2p-shell">
          <div className="i2p-heading">
            <Title level={3}>
              Convert <b>Images</b> to <b>PDF</b>
            </Title>
            <Paragraph>
              Upload one or more images, review them in a grid, rotate or remove
              any image, and then combine everything into one PDF.
            </Paragraph>
          </div>

          <input
            ref={inputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.bmp,.tiff,.gif,.webp"
            multiple
            hidden
            onChange={handleFileInput}
          />

          {imageItems.length === 0 ? (
            <div
              className={`i2p-empty ${isDragOver ? "drag-over" : ""}`}
              onClick={openFilePicker}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
            >
              <div className="i2p-empty-icon">
                <UploadOutlined />
              </div>
              <div className="i2p-empty-title">Choose Images</div>
              <div className="i2p-empty-sub">
                Select multiple JPG, PNG, WEBP and more, or drag them here.
              </div>
              <button className="i2p-primary-btn" type="button">
                Choose Images
              </button>
            </div>
          ) : (
            <>
              <div className="i2p-workspace">
                <div className={`i2p-grid-panel ${uploading ? "locked" : ""}`}>
                  <div className="i2p-grid-header">
                    <div>
                      <div className="i2p-grid-title">
                        Selected Images ({imageItems.length})
                      </div>
                      <div className="i2p-grid-sub">
                        Rotate, remove, or add more images before converting.
                      </div>
                    </div>
                  </div>

                  <div className="i2p-grid">
                    {imageItems.map((item) => (
                      <div key={item.id} className="i2p-card">
                        <div className="i2p-card-actions">
                          <button
                            type="button"
                            className="i2p-card-icon"
                            onClick={() => handleRotate(item.id)}
                            title="Rotate image"
                            disabled={uploading}
                          >
                            <RotateRightOutlined />
                          </button>
                          <button
                            type="button"
                            className="i2p-card-icon danger"
                            onClick={() => handleRemove(item.id)}
                            title="Remove image"
                            disabled={uploading}
                          >
                            <CloseOutlined />
                          </button>
                        </div>
                        <div className="i2p-card-preview">
                          <img
                            src={item.previewUrl}
                            alt={item.file.name}
                            style={{
                              transform: `rotate(${item.rotation}deg)`,
                            }}
                          />
                        </div>
                        <div className="i2p-card-name" title={item.file.name}>
                          {item.file.name}
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      className="i2p-add-card"
                      onClick={openFilePicker}
                      disabled={uploading}
                    >
                      <span className="i2p-add-icon">
                        <PlusOutlined />
                      </span>
                      <span className="i2p-add-text">Add more images</span>
                    </button>
                  </div>
                </div>

                <aside className="i2p-side-panel">
                  <div className="i2p-side-card">
                    <div className="i2p-side-title">Image to PDF options</div>
                    <div className="i2p-side-note">
                      The selected images will be merged into one PDF in the
                      order shown here.
                    </div>

                    <div className="i2p-summary">
                      <div className="i2p-summary-row">
                        <span>Images selected</span>
                        <strong>{imageItems.length}</strong>
                      </div>
                      <div className="i2p-summary-row">
                        <span>Ready to convert</span>
                        <strong>Yes</strong>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="i2p-convert-btn"
                      onClick={handleConvert}
                      disabled={uploading || imageItems.length === 0}
                    >
                      {uploading ? (
                        <>
                          <span className="i2p-spinner" />
                          Converting...
                        </>
                      ) : (
                        "Convert to PDF"
                      )}
                    </button>

                    <OfficePdfResultActions
                      downloadUrl={downloadUrl}
                      originalFileName={convertedFileName}
                      requiresAuth={isModule}
                      stacked
                      className="i2p-result-actions"
                    />
                  </div>
                </aside>
              </div>
            </>
          )}
        </div>
      </div>

      {!isModule && <FooterLayout />}
    </>
  );
};

export default Image2Pdf;
